import React, { useState, useEffect, useCallback, useRef } from "react";
import Editor, { useMonaco } from "@monaco-editor/react";
import * as Babel from "@babel/standalone";
import { createRoot } from "react-dom/client";
import "./reactLiveCodeEditor.css";
import { toast } from "react-toastify";
import { useWebSocket } from "./WebSocketContext";
import PromptHireLogo from "./assets/prompthire_logo.png";
import BotBackground from "./assets/Background.png";
import ConfirmationModal from "./ConfirmationModal";
import Split from "react-split";
import { CALL_END } from "./assets/svgComponent";
import ReactCodeEditorinput from "./ReactInputDelete";
import logger from './utils/logger.js';

// Custom debounce function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Unknown error" };
  }
  componentDidCatch(error, info) {
    logger.error("Runtime error caught in ErrorBoundary:", error, info);
  }
  handleReset = () => {
    this.setState({ hasError: false, message: "" });
  };
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ color: "red", padding: "10px", background: "#fff0f0" }}>
          <strong>Runtime Error:</strong>
          <pre style={{ whiteSpace: "pre-wrap" }}>{this.state.message}</pre>
          <button onClick={this.handleReset} style={{ marginTop: "10px" }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ReactLiveCodeEditor({ interviewId, question }) {
  // Monaco Editor & Theme Configuration
  const monaco = useMonaco();
  const [editorTheme, setEditorTheme] = useState("vs-dark");
  const [textColor, setTextColor] = useState("#ffffff");

  // File & Editor State
  const [models, setModels] = useState({});
  const [filesClick, setFilesClick] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [preview, setPreview] = useState(null);
  const previewRef = useRef(null);
  const rootRef = useRef(null);

  // Files & Questions
  const [question_bank, setquestion_bank] = useState([]);
  const [errors, setErrors] = useState({});

  // Timer & Autosave System
  const [timeParts, setTimeParts] = useState({ minutes: "00", seconds: "00" });
  const [countdown, setCountdown] = useState(null);
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(Date.now());
  const timerIntervalRef = useRef(null);
  const [isTimeWarningOpen, setIsTimeWarningOpen] = useState(false);
  const [warningTimeMinutes, setWarningTimeMinutes] = useState(5);
  const [hasShownFiveMinWarning, setHasShownFiveMinWarning] = useState(false);
  const [hasShownOneMinWarning, setHasShownOneMinWarning] = useState(false);

  // Submission & Save Management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);

  // Console & Error Output
  const [console2, setConsole] = useState(false);
  const [error, setError] = useState(null);

  // Hint System
  const [isHintClicked, setIsHintClicked] = useState(false);
  const [isHintModalOpen, setIsHintModalOpen] = useState(false);
  const [isHintDisabled, setIsHintDisabled] = useState(false);
  const hintTimeoutRef = useRef(null);
  const [transformedCode, setTransformedCode] = useState("");
  const [isEndCallModalOpen, setIsEndCallModalOpen] = useState(false);
  // UI Layout State
  const [showFileExplorer, setShowFileExplorer] = useState(true);
  const [isMinimised, setMinimised] = useState(false);

  // EVA AI Assistant
  const [evaSpeaking, setEvaSpeaking] = useState(false);
  const [hintCount, setHintCount] = useState(0);
  const maxHints = 3; // Maximum allowed hints

  const {
    sendDebugHintRequest,
    id,
    sendApproachHintRequest,
    sendSkipRequest,
    questionTimer,
    boilerplateCode,
    callEnded,
  } = useWebSocket();
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL;
  const currentQuestionId = id;
  const leaveCall = () => {
    //end_interview_call
    callEnded(); // Assuming this is defined elsewhere
    window.dispatchEvent(new CustomEvent("end_interview_call"));
    localStorage.removeItem(getLocalStorageKey("editor-logs"));
  };

  // Handler for opening the end call modal
  const handleEndCallClick = () => {
    setIsEndCallModalOpen(true);
  };

  // Handler for closing the end call modal
  const handleCloseEndCallModal = () => {
    setIsEndCallModalOpen(false);
  };

  // Handler for confirming the end call
  // const handleEndCallConfirm = () => {
  //   leaveCall();

  //   setIsEndCallModalOpen(false);
  // };
  const handleEndCallConfirm = () => {
    // End the interview call
    leaveCall();

    // Close the modal
    setIsEndCallModalOpen(false);

    // Stop the timer after modal is closed
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Remove saved timer from localStorage
    const savedTimerKey = getLocalStorageKey("timer");
    localStorage.setItem(savedTimerKey, seconds.toString());

    // Update countdown state
    setCountdown(savedTimerKey);
  };
  const appJs = `import React from 'react';
export default function App() {
  console.log("App component loaded");
  return <div>Todo App</div>;
}`;

  // Initialize defaultFiles with boilerplateCode["App.js"] if available
  const defaultFiles = {
    "App.js":
      boilerplateCode && boilerplateCode["App.js"]
        ? boilerplateCode["App.js"]
        : appJs,
  };

  const getLocalStorageKey = (type) =>
    `reactEditor_${type}_${interviewId}_${currentQuestionId}`;
  const [files, setFiles] = useState(() => {
    const saved = localStorage.getItem(getLocalStorageKey("editor-files"));
    return saved ? JSON.parse(saved) : defaultFiles;
  });
  const [currentFile, setCurrentFile] = useState(() => {
    return localStorage.getItem("editor-currentFile") || "App.js";
  });
  const [logs, setLogs] = useState(() => {
    const saved = localStorage.getItem("editor-logs");
    return saved ? JSON.parse(saved) : [];
  });
  const [submittedQuestions, setSubmittedQuestions] = useState({});

  // Create debounced handleSave
  const debouncedHandleSave = useCallback(
    debounce(() => {
      if (isDirty) {
        handleSave();
      }
    }, 500),
    [isDirty]
  );

  const theme = {
    dark: {
      background: "#1e1e1e",
      surface: "#252526",
      border: "#404040",
      text: "#ffffff",
      textSecondary: "#888888",
      primary: "#4CAF50",
      secondary: "#2d2d2d",
      editor: "vs-dark",
      fileExplorer: "#1e1e1e",
      console: "#1e1e1e",
      accent: "#4CAF50",
      hover: "#2d2d2d",
      activeTab: "#1e1e1e",
      inactiveTab: "#252526",
    },
    light: {
      background: "#ffffff",
      surface: "#f5f5f5",
      border: "#e0e0e0",
      text: "#333333",
      textSecondary: "#666666",
      primary: "#2196F3",
      secondary: "#eeeeee",
      editor: "light",
      fileExplorer: "#f8f8f8",
      console: "#f8f8f8",
      accent: "#2196F3",
      hover: "#e8e8e8",
      activeTab: "#ffffff",
      inactiveTab: "#f5f5f5",
    },
  };
  const currentTheme = editorTheme === "vs-dark" ? theme.dark : theme.light;
  const addLog = (message, color = "inherit") => {
    setLogs((prev) => {
      if (prev.length > 0 && prev[prev.length - 1].message === message) {
        return prev;
      }
      return [
        ...prev,
        { message, timestamp: new Date().toLocaleTimeString(), color },
      ];
    });
  };
  const compileAndRun = (file, isMainApp) => {
    try {
      const compiledModules = {};
      const scopedConsole = {
        log: (...args) => addLog(args.join(" ")),
        error: (...args) => addLog("Error: " + args.join(" ")),
      };
      for (const [filename, code] of Object.entries(files)) {
        const instrumentedCode = code
          .replace(/console\.log/g, "scopedConsole.log")
          .replace(/console\.error/g, "scopedConsole.error");
        const compiled = Babel.transform(instrumentedCode, {
          presets: ["react", "env"],
        }).code;
        compiledModules[filename] = {
          code: compiled,
          exports: {},
          evaluated: false,
        };
      }
      const customRequire = (modName) => {
        if (modName === "react") return React;
        if (modName.startsWith("./")) {
          const target = modName.slice(2);
          const mod = compiledModules[target];
          if (!mod) throw new Error("Module not found: " + modName);
          if (!mod.evaluated) {
            const module = { exports: {} };
            const fn = new Function(
              "exports",
              "module",
              "require",
              "React",
              "scopedConsole",
              "logger",
              mod.code
            );
            fn(module.exports, module, customRequire, React, scopedConsole, logger);
            mod.exports = module.exports;
            mod.evaluated = true;
          }
          return mod.exports;
        }
        throw new Error("Cannot find module: " + modName);
      };
      const mod = compiledModules[file];
      const module = { exports: {} };
      const fn = new Function(
        "exports",
        "module",
        "require",
        "React",
        "scopedConsole",
        "logger",
        mod.code
      );
      fn(module.exports, module, customRequire, React, scopedConsole, logger);
      mod.exports = module.exports;
      mod.evaluated = true;
      if (isMainApp && previewRef.current) {
        const Component = mod.exports.default;
        previewRef.current.innerHTML = "";
        
        // Create root only once and reuse it
        if (!rootRef.current) {
          rootRef.current = createRoot(previewRef.current);
        }
        
        // Check if the root is still valid (container hasn't changed)
        try {
          rootRef.current.render(
            <ErrorBoundary>
              <Component />
            </ErrorBoundary>
          );
        } catch (error) {
          // If root is invalid, create a new one
          if (rootRef.current) {
            try {
              rootRef.current.unmount();
            } catch (e) {
              // Ignore unmount errors
            }
          }
          rootRef.current = createRoot(previewRef.current);
          rootRef.current.render(
            <ErrorBoundary>
              <Component />
            </ErrorBoundary>
          );
        }
      }
      setErrors((prev) => ({ ...prev, [file]: "" }));
    } catch (err) {
      if (previewRef.current && file === "App.js") {
        previewRef.current.innerHTML = `<div style="color: red; white-space: pre-wrap;">${err.message}</div>`;
      }
      setErrors((prev) => ({ ...prev, [file]: err.message }));
    }
  };
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null, // "create" | "delete"
    fileName: null,
  });

  const createNewFile = () => {
    setConfirmModal({ isOpen: true, action: "create", fileName: "" });
  };

  const deleteFile = (name) => {
    if (name === "App.js") {
      toast.error(`${name} cannot be deleted.`);
      return;
    }
    if (Object.keys(files).length === 1) {
      toast.error("At least one file is required.");
      return;
    }
    setConfirmModal({ isOpen: true, action: "delete", fileName: name });
  };

  const handleModalConfirm = (inputValue) => {
    const { action } = confirmModal;

    if (action === "create") {
      if (!inputValue || !inputValue.trim()) {
        setConfirmModal({
          isOpen: true,
          action: "error",
          fileName: null,
          errorMessage: "Please enter a valid file name",
        });
        return;
      }

      // Check if file has an extension
      if (!inputValue.includes(".")) {
        setConfirmModal({
          isOpen: true,
          action: "error",
          fileName: null,
          errorMessage:
            "Please include a file extension (.js, .jsx, .ts, or .tsx)",
        });
        return;
      }

      const extension = inputValue.split(".").pop().toLowerCase();
      const base = inputValue.replace(/\.[^/.]+$/, "");
      let content = "";

      // Strict check for supported extensions
      const supportedExtensions = ["js", "jsx", "ts", "tsx"];
      if (!supportedExtensions.includes(extension)) {
        setConfirmModal({
          isOpen: true,
          action: "error",
          fileName: null,
          errorMessage: `"${extension}" is not a valid extension. Only ${supportedExtensions
            .map((ext) => `.${ext}`)
            .join(", ")} files are allowed.`,
        });
        return;
      }

      // Check if file already exists
      if (files[inputValue]) {
        setConfirmModal({
          isOpen: true,
          action: "error",
          fileName: null,
          errorMessage: `File "${inputValue}" already exists`,
        });
        return;
      }

      if (extension === "js") {
        content = `export default function ${base}() {\n  return "${base} Component";\n}`;
      } else if (["jsx", "tsx", "ts"].includes(extension)) {
        content = `import React from 'react';\n\nexport default function ${base}() {\n  return <div>${base} Component</div>;\n}`;
      }

      setFiles((prev) => ({
        ...prev,
        [inputValue]: content,
      }));
      setCurrentFile(inputValue);
      addLog(`Created and saved new file: ${inputValue}`);
      setConfirmModal({ isOpen: false, action: null, fileName: null });
    } else if (action === "delete") {
      setFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[confirmModal.fileName];
        return newFiles;
      });
      if (currentFile === confirmModal.fileName) {
        const keys = Object.keys(files).filter(
          (f) => f !== confirmModal.fileName
        );
        setCurrentFile(keys[0]);
      }
      addLog(`Deleted and updated file: ${confirmModal.fileName}`);
      setConfirmModal({ isOpen: false, action: null, fileName: null });
    } else if (action === "error") {
      // Handle error modal close
      setConfirmModal({ isOpen: false, action: null, fileName: null });
    }
  };

  const isQuestionSubmitted =
    (currentQuestionId &&
      submittedQuestions[interviewId]?.includes(currentQuestionId)) ||
    localStorage.getItem(getLocalStorageKey("submitted")) === "true";
  const toggleConsole = useCallback(() => {
    setConsole((prev) => !prev);
  }, []);

  const SaveButton = () => (
    <button
      onClick={handleSave}
      disabled={!isDirty || isSubmitting || isQuestionSubmitted}
      style={{
        padding: "8px 16px",
        backgroundColor:
          !isDirty || isSubmitting || isQuestionSubmitted
            ? currentTheme.secondary
            : currentTheme.accent,
        color:
          !isDirty || isSubmitting || isQuestionSubmitted
            ? currentTheme.textSecondary
            : "#fff",
        border: "none",
        borderRadius: "6px",
        cursor:
          !isDirty || isSubmitting || isQuestionSubmitted
            ? "not-allowed"
            : "pointer",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        opacity: isQuestionSubmitted ? 0.5 : 1,
      }}
    >
      <span style={{ fontSize: "16px" }}>💾</span>
      Save
    </button>
  );

  // const handleSubmitClick = () => {
  //   if (isAutoSubmitted) {
  //     return;
  //   }
  //   setIsSubmitModalOpen(true);
  // };
  const handleSubmitClick = () => {
    if (isQuestionSubmitted) {
      toast.error("This question has already been submitted. You cannot submit it again.");
      return;
    }
    if (isAutoSubmitted) {
      return;
    }
    setIsSubmitModalOpen(true);
  };
  const handleSave = () => {
    try {
      localStorage.setItem(
        getLocalStorageKey("editor-files"),
        JSON.stringify(files)
      );
      logger.debug(
        "Saved files to localStorage with key:",
        getLocalStorageKey("editor-files")
      );
      setIsDirty(false);
      setSaveStatus("Saved!");
      addLog("Changes saved successfully", "green");
      setTimeout(() => setSaveStatus(""), 2000);
    } catch (err) {
      addLog(`Error saving files: ${err.message}`, "salmon");
      setSaveStatus("Save Failed");
      setTimeout(() => setSaveStatus(""), 2000);
    }
  };

  const handleSkipQuestion = () => {
    setIsSkipModalOpen(true);
  };

  const handleCloseSkipModal = () => {
    setIsSkipModalOpen(false);
  };

  const handleSkipConfirm = () => {
    sendSkipRequest();
    setIsSkipModalOpen(false);
  };

  const handleCloseTimeWarning = () => {
    setIsTimeWarningOpen(false);
  };

  const handleCloseSubmitModal = () => {
    setIsSubmitModalOpen(false);
  };

  const handleSubmitConfirm = async () => {
    if (isQuestionSubmitted) {
      toast.error("This question has already been submitted. You cannot submit it again.");
      setIsSubmitModalOpen(false);
      return;
    }
    setIsSubmitting(true);

    try {
      logger.debug("Submitting files:", Object.keys(files));
      const rawData = {
        interview_id: interviewId,
        question_id: currentQuestionId,
        answer: { files: files },
        question: question,
      };
      logger.debug("Submitting code with data:", rawData);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      const res = await fetch(`https://${BASE_URL}/api/react-coding-question`, {
        method: "post",
       // credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(rawData),
      });

      if (!res.ok) {
        throw new Error(`API request failed with status ${res.status}`);
      }

      // Set submitted state before updating localStorage
      setSubmittedQuestions((prev) => ({
        ...prev,
        [interviewId]: [...(prev[interviewId] || []), currentQuestionId],
      }));
      setIsQuestionSubmitted(true); // Add this line to ensure local state is updated
      localStorage.setItem(getLocalStorageKey("submitted"), "true");
      
      const saved = JSON.parse(localStorage.getItem(getLocalStorageKey("submittedQuestions"))) || {};
      localStorage.setItem(
        getLocalStorageKey("submittedQuestions"),
        JSON.stringify({
          ...saved,
          [interviewId]: [...(saved[interviewId] || []), currentQuestionId],
        })
      );

      if (isSubmitModalOpen) {
        setIsSubmitModalOpen(false);
      }
      toast.success("Code submitted successfully!");

    } catch (error) {
      logger.error("Error submitting code:", error);
      toast.error("Failed to submit code.");
      setIsSubmitModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHintButtonClick = () => {
    if (!isHintClicked && !isHintDisabled) {
      setIsHintModalOpen(true);
    }
  };

  // const handleHintSelection = (isDebugHint) => {
  //   setIsHintClicked(true);
  //   setIsHintModalOpen(false);
  //   const currentFileContent = files[currentFile] || "";
  //   if (isDebugHint) {
  //     sendDebugHintRequest(currentFileContent, currentQuestionId);
  //   } else {
  //     sendApproachHintRequest(currentFileContent, currentQuestionId);
  //   }
  //   if (hintTimeoutRef.current) {
  //     clearTimeout(hintTimeoutRef.current);
  //   }
  //   hintTimeoutRef.current = setTimeout(() => {
  //     setIsHintClicked(false);
  //   }, 4000);
  // };

  const handleHintSelection = (isDebugHint) => {
    if (hintCount >= maxHints) {
      toast.error(`You've reached the maximum of ${maxHints} hints`);
      setIsHintModalOpen(false);
      setIsHintDisabled(true);
      return;
    }

    setIsHintClicked(true);
    setIsHintModalOpen(false);
    const currentFileContent = files[currentFile] || "";

    if (isDebugHint) {
      sendDebugHintRequest(currentFileContent, currentQuestionId);
    } else {
      sendApproachHintRequest(currentFileContent, currentQuestionId);
    }

    // Increment hint count
    setHintCount((prev) => prev + 1);

    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current);
    }
    hintTimeoutRef.current = setTimeout(() => {
      setIsHintClicked(false);
    }, 4000);
  };

  const handleCloseHintModal = () => {
    setIsHintModalOpen(false);
  };

  const autoSaveCode = useCallback(async () => {
    try {
      if (!interviewId || !question || !currentQuestionId) {
        logger.debug("Missing interviewId, question, or id for auto-save");
        return;
      }
      logger.debug("Auto-save triggered at", new Date().toLocaleTimeString());
      const payload = {
        interview_id: interviewId,
        question_id: currentQuestionId,
        question: question,
        answer: {
          files,
        },
        language: "JavaScript",
      };
      const response = await fetch(`https://${BASE_URL}/api/auto-save-code`, {
        method: "POST",
       // credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        logger.error("Failed to auto-save code:", response.statusText);
      } else {
        logger.debug(
          "Code auto-saved successfully at",
          new Date().toLocaleTimeString()
        );
        setLastAutoSaveTime(Date.now());
      }
    } catch (error) {
      logger.error("Error auto-saving code:", error);
    }
  }, [currentQuestionId, interviewId, question, files, BASE_URL]);

  const parseTimeToSeconds = (timeString) => {
    if (!timeString) return 0;
    try {
      const [minutes, seconds] = timeString
        .split(":")
        .map((num) => parseInt(num, 10));
      if (isNaN(minutes) || isNaN(seconds)) return 0;
      return minutes * 60 + seconds;
    } catch (error) {
      logger.error("Error parsing time string:", error);
      return 0;
    }
  };

  const formatSecondsToTime = (totalSeconds) => {
    if (totalSeconds <= 0) return "00:00";
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const startCountdown = (initialSeconds) => {
    logger.debug("Starting countdown with seconds:", initialSeconds,);
    if (isQuestionSubmitted) {
      setCountdown(savedTimerKey);
      return;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    let seconds = initialSeconds;
    if (seconds === undefined && countdown) {
      seconds = parseTimeToSeconds(countdown);
    }
    if (seconds <= 0 || isNaN(seconds)) {
      logger.error("Invalid countdown value:", seconds);
      return;
    }
    setCountdown(formatSecondsToTime(seconds));
    const savedTimerKey = getLocalStorageKey("timer");
    localStorage.setItem(savedTimerKey, seconds.toString());
    logger.debug("Saved initial timer value to localStorage:", seconds);
    timerIntervalRef.current = setInterval(() => {
      seconds -= 1;
      localStorage.setItem(savedTimerKey, seconds.toString());
      if (seconds <= 0) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        setCountdown("0:00");
        setIsTimeWarningOpen(false);
        setIsHintDisabled(true);
        const handleAutoSubmit = async () => {
          if (isQuestionSubmitted) {
            toast.error("This question has already been submitted. You cannot submit it again.");
            setIsSubmitModalOpen(false);
            return;
          }
          setIsSubmitting(true);
          
          try {
   
            const rawData = {
              interview_id: interviewId,
              question_id: currentQuestionId,
              answer: { files: files },
              question: question,
            };
            logger.debug("Auto-submitting with latest data:", rawData);
            const res = await fetch(
              `https://${BASE_URL}/api/react-coding-question`,
              {
                method: "post",
               // credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(rawData),
              }
            );
            if (!res.ok) {
              throw new Error(`API request failed with status ${res.status}`);
            }
            setSubmittedQuestions((prev) => ({
              ...prev,
              [interviewId]: [...(prev[interviewId] || []), currentQuestionId],
            }));
            localStorage.setItem(getLocalStorageKey("submitted"), "true");
            setIsAutoSubmitted(true);
            localStorage.setItem(savedTimerKey, "00.00");
            logger.debug("Code auto-submitted due to time expiration!");
          } catch (error) {
            logger.error("Error auto-submitting code:", error);
            toast.error("Failed to auto-submit code.");
          } finally {
            setIsSubmitting(false);
          }
        };
        handleAutoSubmit(files,isQuestionSubmitted);
 
        // localStorage.removeItem(savedTimerKey);
        
        logger.debug("Timer expired, removed saved timer from localStorage");
      } else {
        setCountdown(formatSecondsToTime(seconds));
        if (seconds === 300 && !hasShownFiveMinWarning) {
          setWarningTimeMinutes(5);
          setIsTimeWarningOpen(true);
          setHasShownFiveMinWarning(true);
          localStorage.setItem(
            getLocalStorageKey(`five_min_warning${interviewId}`),
            "true"
          );
          logger.debug("Showing 5-minute warning");
        } else if (seconds === 60 && !hasShownOneMinWarning) {
          setWarningTimeMinutes(1);
          setIsTimeWarningOpen(true);
          setHasShownOneMinWarning(true);
          localStorage.setItem(
            getLocalStorageKey(`one_min_warning${interviewId}`),
            "true"
          );
          logger.debug("Showing 1-minute warning");
        }
      }
    }, 1000);
  };
  useEffect(() => {
    // Check if this question was already submitted
    const isSubmitted = localStorage.getItem(getLocalStorageKey("submitted")) === "true";
    if (isSubmitted && currentQuestionId) {
      setSubmittedQuestions(prev => ({
        ...prev,
        [interviewId]: [...(prev[interviewId] || []), currentQuestionId]
      }));
    }
  }, [currentQuestionId, interviewId]);
  const renderTimer = (timeLeft) => {
    const seconds =
      typeof timeLeft === "string" ? parseTimeToSeconds(timeLeft) : timeLeft;
    const isWarning = seconds <= 30;
    return (
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "35%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "6px 16px",
          borderRadius: "20px",
          backgroundColor: isWarning ? "#ffeeee" : "#f8f9fa",
          boxShadow: isWarning
            ? "0 2px 8px rgba(255,0,0,0.2)"
            : "0 2px 4px rgba(0,0,0,0.1)",
          border: isWarning ? "1px solid #ffcccc" : "1px solid #e9ecef",
          fontWeight: "bold",
          fontSize: "14px",
          letterSpacing: "0.5px",
          color: seconds <= 60 ? "#FF3333" : "#343a40",
          animation: isWarning ? "timerPulse 1s infinite" : "none",
          zIndex: 1002,
        }}
      >
        <span
          role="img"
          aria-label="Timer"
          style={{ marginRight: "8px", fontSize: "16px" }}
        >
          ⏱️
        </span>
        {typeof timeLeft === "string" ? timeLeft : formatSecondsToTime(seconds)}
      </div>
    );
  };

  const renderPreview = () => (
    <div
      style={{
        padding: "1rem",
        width: "100%",
        backgroundColor: currentTheme.surface,
        color: currentTheme.text,
        borderLeft: `1px solid ${currentTheme.border}`,
        overflowY: "auto",
        boxShadow: `0 2px 4px ${
          editorTheme === "vs-dark"
            ? "rgba(0,0,0,0.2)"
            : "rgba(0,0,0,0.1)"
        }`,
      }}
    >
      <h3
        style={{
          textAlign: "center",
          fontSize: "16px",
          padding: "12px 0",
          letterSpacing: "0.5px",
          color: currentTheme.text,
          borderBottom: `1px solid ${currentTheme.border}`,
          marginBottom: "1rem",
          fontWeight: "500",
        }}
      >
        🔍 Output: App.js
      </h3>

      <div
        ref={previewRef}
        style={{ 
          marginTop: "5px", 
          color: currentTheme.text,
          padding: "1rem",
          backgroundColor: currentTheme.background,
          borderRadius: "8px",
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />

      {errors["App.js"] && (
        <div
          style={{
            background: editorTheme === "vs-dark" ? "#3a1f1f" : "#fee2e2",
            color: editorTheme === "vs-dark" ? "#fecaca" : "#991b1b",
            padding: "1rem",
            borderRadius: "8px",
            marginTop: "1rem",
            border: `1px solid ${editorTheme === "vs-dark" ? "#7f1d1d" : "#fecaca"}`,
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px",
            marginBottom: "8px",
            fontWeight: "500",
          }}>
            <span>⚠️</span>
            <span>Error</span>
          </div>
          <pre style={{ 
            margin: 0, 
            whiteSpace: "pre-wrap",
            fontFamily: "monospace",
          }}>
            {errors["App.js"]}
          </pre>
        </div>
      )}
    </div>
  );

  // useEffect Hooks
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      setTimeParts({ minutes, seconds });
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (boilerplateCode && typeof boilerplateCode === "object") {
      const fileNames = Object.keys(boilerplateCode);
      const data = fileNames
        .filter((key) => key !== "App.js") // Exclude App.js from question_bank
        .map((key) => ({
          title: key,
          description: boilerplateCode[key],
        }));
      logger.debug("File names:", fileNames);
      logger.debug("Question bank data:", data);
      setquestion_bank(data);
    } else {
      logger.debug("Boiler Plate not found or invalid");
      setquestion_bank([]);
    }
  }, [boilerplateCode]);

  useEffect(() => {
    logger.debug(question_bank, "fetched question_bank");
  }, [question_bank]);

  useEffect(() => {
    if (question_bank.length === 0) return;
    const question_bankFiles = question_bank.reduce((acc, item) => {
      const safeTitle = item.title.replace(/[<>:"/\\|?*\n]/g, "");
      const fileName = `${safeTitle || "question_bank"}`;
      if (files[fileName] && files[fileName].includes(item.description)) {
        return acc;
      }
      acc[fileName] = `/* ${item.title}\n\n${item.description}\n*/`;
      return acc;
    }, {});
    setFiles((prev) => {
      const updatedFiles = {
        ...prev,
        ...question_bankFiles,
      };
      localStorage.setItem(
        getLocalStorageKey("editor-files"),
        JSON.stringify(updatedFiles)
      );
      return updatedFiles;
    });
  }, [question_bank]);

  useEffect(() => {
    localStorage.setItem(
      getLocalStorageKey("editor-files"),
      JSON.stringify(files)
    );
  }, [files]);

  useEffect(() => {
    localStorage.setItem("editor-currentFile", currentFile);
  }, [currentFile]);

  useEffect(() => {
    localStorage.setItem("editor-logs", JSON.stringify(logs));
  }, [logs]);

  useEffect(() => {
    if (!monaco) return;
    const newModels = {};
    for (const [name, code] of Object.entries(files)) {
      const uri = monaco.Uri.parse(`file:///${name}`);
      let model = monaco.editor.getModel(uri);
      if (!model) {
        model = monaco.editor.createModel(code, "javascript", uri);
      }
      newModels[name] = model;
    }
    setModels(newModels);
  }, [monaco, files]);

  useEffect(() => {
    const model = models[currentFile];
    if (!model) return;
    const listener = model.onDidChangeContent(() => {
      let updatedCode = model.getValue();
      if (currentFile === "App.js" && !updatedCode.includes("import React")) {
        updatedCode = `import React from 'react';\n\n` + updatedCode;
        model.setValue(updatedCode);
      }
      setFiles((prev) => ({
        ...prev,
        [currentFile]: updatedCode,
      }));
      setIsDirty(true);
      debouncedHandleSave();
    });
    return () => listener.dispose();
  }, [models, currentFile, debouncedHandleSave]);

  useEffect(() => {
    const timer = setTimeout(() => {
      compileAndRun("App.js", true);
    }, 500);
    return () => clearTimeout(timer);
  }, [files, currentFile]);

  useEffect(() => {
    if (currentFile !== "App.js") {
      compileAndRun(currentFile, false);
    }
  }, [currentFile, files]);

  useEffect(() => {
    if (currentQuestionId) {
      setIsHintClicked(false);
      setIsHintDisabled(false);
      setIsHintModalOpen(false);
    }
  }, [currentQuestionId]);

  useEffect(() => {
    if (countdown === "0:00") {
      setIsHintDisabled(true);
    }
  }, [countdown]);

  useEffect(() => {
    return () => {
      if (hintTimeoutRef.current) {
        clearTimeout(hintTimeoutRef.current);
        hintTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    const savedTimerKey = getLocalStorageKey("timer");
    const savedTimerValue = localStorage.getItem(savedTimerKey);
    if (savedTimerValue) {
      const savedSeconds = parseInt(savedTimerValue, 10);
      logger.debug(
        "Found saved timer value:",
        savedSeconds,
        "seconds for question ID:",
        currentQuestionId
      );
      if (savedSeconds > 0 && !isAutoSubmitted) {
        setCountdown(formatSecondsToTime(savedSeconds));
        const timerId = setTimeout(() => {
          startCountdown(savedSeconds);
        }, 50);
        return () => {
          clearTimeout(timerId);
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
        };
      }
    } else if (questionTimer && !isAutoSubmitted) {
      logger.debug(
        "No saved timer, using questionTimer:",
        questionTimer,
        "for question ID:",
        currentQuestionId
      );
      const seconds = parseTimeToSeconds(questionTimer);
      logger.debug("Parsed seconds:", seconds);
      if (seconds > 0) {
        localStorage.setItem(savedTimerKey, seconds.toString());
        setCountdown(formatSecondsToTime(seconds));
        const timerId = setTimeout(() => {
          startCountdown(seconds,files);
        }, 50);
        return () => {
          clearTimeout(timerId);
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
        };
      } else {
        logger.warn("Received invalid timer value:", questionTimer);
      }
    } else {
      logger.debug(
        "No timer received in ReactLiveCodeEditor, clearing countdown"
      );
      setCountdown(null);
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [questionTimer, currentQuestionId, files, isAutoSubmitted]);

  useEffect(() => {
    if (questionTimer && timerIntervalRef.current) {
      logger.debug(
        "Ignoring new timer value from WebSocket as timer is already running"
      );
    }
  }, [questionTimer]);

  useEffect(() => {
    if (currentQuestionId) {
      logger.debug(
        "ReactLiveCodeEditor: Question ID changed to:",
        currentQuestionId
      );
      setIsTimeWarningOpen(false);
      const fiveMinWarningShown =
        localStorage.getItem(
          getLocalStorageKey(`five_min_warning${interviewId}`)
        ) === "true";
      const oneMinWarningShown =
        localStorage.getItem(
          getLocalStorageKey(`one_min_warning${interviewId}`)
        ) === "true";
      setHasShownFiveMinWarning(fiveMinWarningShown);
      setHasShownOneMinWarning(oneMinWarningShown);
      const savedFiles = localStorage.getItem(
        getLocalStorageKey("editor-files")
      );
      if (!savedFiles) {
        logger.debug(
          "No saved files found for new question ID, will use default template"
        );
      } else {
        logger.debug("Found saved files for question ID:", currentQuestionId);
      }
    }
  }, [currentQuestionId, interviewId]);

  useEffect(() => {
    if (interviewId) {
      const savedForInterview = localStorage.getItem(
        getLocalStorageKey("submittedQuestions")
      );
      if (savedForInterview) {
        setSubmittedQuestions(JSON.parse(savedForInterview));
      } else {
        setSubmittedQuestions({});
      }
    }
  }, [interviewId]);

  useEffect(() => {
    const speakingInterval = setInterval(() => {
      setEvaSpeaking((prev) => !prev);
    }, 5000);
    return () => clearInterval(speakingInterval);
  }, []);

  useEffect(() => {
    if (!interviewId || !question || !currentQuestionId || isQuestionSubmitted)
      return;
    logger.debug(
      "Setting up auto-save interval at",
      new Date().toLocaleTimeString()
    );
    const autoSaveInterval = setInterval(() => {
      logger.debug(
        "3-minute interval triggered at",
        new Date().toLocaleTimeString()
      );
      autoSaveCode();
    }, 3 * 60 * 1000);
    return () => {
      logger.debug("Clearing auto-save interval");
      clearInterval(autoSaveInterval);
    };
  }, [autoSaveCode, interviewId, question, currentQuestionId]);

  // Load hint count from localStorage when question changes
  useEffect(() => {
    const savedHintCount = localStorage.getItem(
      getLocalStorageKey("hintCount")
    );
    if (savedHintCount) {
      setHintCount(parseInt(savedHintCount, 10));
    } else {
      setHintCount(0);
    }
  }, [currentQuestionId]);
  useEffect(() => {
    if (interviewId) {
      const savedForInterview = localStorage.getItem(
        getLocalStorageKey("submittedQuestions")
      );
      if (savedForInterview) {
        setSubmittedQuestions(JSON.parse(savedForInterview));
      } else {
        setSubmittedQuestions({});
      }

      // Also check the specific question submission flag
      const isSubmitted =
        localStorage.getItem(getLocalStorageKey("submitted")) === "true";
      if (isSubmitted && currentQuestionId) {
        setSubmittedQuestions((prev) => ({
          ...prev,
          [interviewId]: [...(prev[interviewId] || []), currentQuestionId],
        }));
      }
    }
  }, [interviewId, currentQuestionId]);
  // Save hint count to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(getLocalStorageKey("hintCount"), hintCount.toString());
  }, [hintCount]);

  // Add useEffect to save CALL_END SVG to localStorage on component mount
  useEffect(() => {
    // Convert SVG to base64 string
    fetch(CALL_END)
      .then(response => response.text())
      .then(svgText => {
        const base64Svg = btoa(svgText);
        localStorage.setItem('callEndSvg', base64Svg);
        logger.debug('CALL_END SVG saved to localStorage');
      })
      .catch(error => {
        logger.error('Error saving CALL_END SVG to localStorage:', error);
      });
  }, []);

  const [callEndSvg, setCallEndSvg] = useState(CALL_END);

  // Add useEffect to load CALL_END SVG from localStorage
  useEffect(() => {
    const savedSvg = localStorage.getItem('callEndSvg');
    if (savedSvg) {
      try {
        const svgText = atob(savedSvg);
        const blob = new Blob([svgText], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        setCallEndSvg(url);
        logger.debug('CALL_END SVG loaded from localStorage');
      } catch (error) {
        logger.error('Error loading CALL_END SVG from localStorage:', error);
        setCallEndSvg(CALL_END); // Fallback to original SVG
      }
    }
  }, []);

  // Cleanup root on unmount
  useEffect(() => {
    return () => {
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
    };
  }, []);

  return (
    <div
      style={{
        backgroundColor: currentTheme.secondary,
      }}
    >
      <style>
        {`
      .hidden-scrollbar {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .hidden-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .wave {
        width: 4px;
        height: 6px;
        background-color: #3b82f6;
        margin: 0 2px;
        border-radius: 2px;
        animation: wave 1.2s ease-in-out infinite;
      }
      .wave:nth-child(2) { animation-delay: 0.2s; }
      .wave:nth-child(3) { animation-delay: 0.4s; }
      .disable-wave {
        width: 4px;
        height: 4px;
        background-color: #6b7280;
        margin: 0 2px;
        border-radius: 2px;
      }
      @keyframes flicker {
        0% { border-color: #3b82f6; }
        50% { border-color: #93c5fd; }
        100% { border-color: #3b82f6; }
      }
      .animate-flicker { animation: flicker 1.5s infinite; }
      .bot-circle {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        alignItems: center;
        position: relative;
      }
      .bot-circle::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 1px solid transparent;
        animation: flicker 1.5s infinite;
      }
      .bot-speaking .bot-circle::before { border-color: #3b82f6; }
      @keyframes wave {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      .wave {
        width: 4px;
        height: 10px;
        background-color: #3b82f6;
        margin: 0 2px;
        border-radius: 2px;
        animation: wave 1.2s ease-in-out infinite;
        display: inline-block;
      }
    `}
      </style>
      {/* header */}
      <div style={{ position: "fixed", width: "100%", zIndex: 100 }}>
        <div
          style={{
            padding: "12px 20px",
            backgroundColor: currentTheme.surface,
            borderBottom: `1px solid ${currentTheme.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 10,
            position: "relative",
            transition: "all 0.2s ease",
            boxShadow: `0 2px 4px ${
              editorTheme === "vs-dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)"
            }`,
          }}
        >
          {isTimeWarningOpen && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
              }}
            >
              <div
                style={{
                  backgroundColor: "#fffbeb",
                  borderRadius: "12px",
                  padding: "24px",
                  maxWidth: "500px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                  borderColor: warningTimeMinutes === 1 ? "#ef4444" : "#f59e0b",
                  borderWidth: "2px",
                  borderStyle: "solid",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    marginBottom: "16px",
                    color: warningTimeMinutes === 1 ? "#b91c1c" : "#b45309",
                  }}
                >
                  Time Warning
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div style={{ fontSize: "32px" }}>⏱️</div>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: warningTimeMinutes === 1 ? "#b91c1c" : "#b45309",
                      fontSize: "18px",
                    }}
                  >
                    {warningTimeMinutes}{" "}
                    {warningTimeMinutes === 1 ? "minute" : "minutes"} remaining!
                  </div>
                  <div
                    style={{
                      color: warningTimeMinutes === 1 ? "#7f1d1d" : "#92400e",
                    }}
                  >
                    You have {warningTimeMinutes}{" "}
                    {warningTimeMinutes === 1 ? "minute" : "minutes"} left to
                    complete and submit your code. Please finish your solution
                    and submit it before time runs out.
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={handleCloseTimeWarning}
                    style={{
                      backgroundColor:
                        warningTimeMinutes === 1 ? "#ef4444" : "#f59e0b",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    I understand
                  </button>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <label
                style={{ cursor: "no-drop", fontSize: 14 }}
                className="animate-pulse"
              >
                <span> 🔴</span>
              </label>
              <label
                onClick={() => setMinimised(true)}
                style={{ cursor: "pointer", fontSize: 14 }}
              >
                <span> 🟡</span>
              </label>
              <label style={{ cursor: "no-drop", fontSize: 14 }}>
                <span> 🟢</span>
              </label>
            </div>
            <button
              onClick={() => {
                setFilesClick(!filesClick);
              }}
              style={{
                marginLeft: 12,
                backgroundColor: "#3a3a3a",
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 600,
                borderRadius: 4,
                padding: "4px 12px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {filesClick ? "📂" : "🗐"}
              <div
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                <span>Files</span>
                <br />
                <span style={{ fontSize: 8, opacity: 0.8 }}>
                  Click to {filesClick ? "Close" : "Open"}
                </span>
              </div>
            </button>
            <h2
              style={{
                margin: "0",
                color: currentTheme.text,
                fontSize: "20px",
                fontWeight: "500",
              }}
            >
              React Live Editor
            </h2>
            {isDirty && (
              <span
                style={{
                  fontSize: "14px",
                  color: "#888",
                  fontStyle: "italic",
                  animation: "fadeIn 0.3s ease-in-out",
                }}
              >
                ● unsaved changes
              </span>
            )}
          </div>
          {countdown && renderTimer(countdown)}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {showFileExplorer ? (
              <div
                style={{
                  opacity: isQuestionSubmitted ? 0.5 : 1,
                  pointerEvents: isQuestionSubmitted ? "none" : "auto",
                }}
              >
                <button
                  onClick={toggleConsole}
                  disabled={isQuestionSubmitted}
                  style={{
                    padding: "8px 12px",
                    gap: "4px",
                    display: "flex",
                    backgroundColor: currentTheme.secondary,
                    color: isQuestionSubmitted
                      ? currentTheme.textDisabled
                      : currentTheme.text,
                    border: "none",
                    borderRadius: "6px",
                    cursor: isQuestionSubmitted ? "not-allowed" : "pointer",
                    opacity: isQuestionSubmitted ? 0.5 : 1,
                    transition: "all 0.2s ease",
                    boxShadow: `0 2px 4px ${
                      editorTheme === "vs-dark"
                        ? "rgba(0,0,0,0.2)"
                        : "rgba(0,0,0,0.1)"
                    }`,
                  }}
                >
                  Console
                  <span
                    style={{
                      display: "inline-block",
                      transition: "transform 0.2s ease",
                      transform: console2 ? "rotate(270deg)" : "rotate(90deg)",
                    }}
                  >
                    ❮❮
                  </span>
                </button>
              </div>
            ) : (
              ""
            )}
            {saveStatus && (
              <div
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  backgroundColor:
                    saveStatus === "Saved!" ? "#4CAF5033" : "#2196F333",
                  color: saveStatus === "Saved!" ? "#4CAF50" : "#2196F3",
                  fontSize: "14px",
                  animation: "fadeIn 0.3s ease-in-out",
                }}
              >
                {saveStatus}
              </div>
            )}
            <SaveButton />
            <button
              onClick={handleHintButtonClick}
              disabled={
                isHintClicked ||
                isQuestionSubmitted ||
                isHintDisabled ||
                hintCount >= maxHints
              }
              style={{
                padding: "4px 8px",
                backgroundColor:
                  isHintClicked ||
                  isQuestionSubmitted ||
                  isHintDisabled ||
                  hintCount >= maxHints
                    ? "#808080"
                    : "#1e40af",
                border: `1px solid ${currentTheme.border}`,
                color: "white",
                borderRadius: "4px",
                cursor:
                  isHintClicked ||
                  isQuestionSubmitted ||
                  isHintDisabled ||
                  hintCount >= maxHints
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                paddingRight: "14px",
                opacity:
                  isHintClicked ||
                  isQuestionSubmitted ||
                  isHintDisabled ||
                  hintCount >= maxHints
                    ? 0.7
                    : 1,
              }}
            >
              <span style={{ fontSize: "14px" }}>💡</span>
              Hint{" "}
              {hintCount < maxHints
                ? `(${maxHints - hintCount} left)`
                : "(Limit reached)"}
            </button>
            <button
              onClick={() => {
                const newTheme =
                  editorTheme === "vs-dark" ? "light" : "vs-dark";
                setEditorTheme(newTheme);
                setTextColor(newTheme === "light" ? "#000000" : "#ffffff");
              }}
              style={{
                padding: "8px",
                backgroundColor: currentTheme.secondary,
                color: currentTheme.text,
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "36px",
                height: "36px",
              }}
            >
              {editorTheme === "vs-dark" ? "☀️" : "🌙"}
            </button>

            <button
              onClick={handleSubmitClick}
              disabled={isSubmitting || isQuestionSubmitted}
              style={{
                padding: "4px 8px",
                backgroundColor: isQuestionSubmitted
                  ? "#4CAF50" // Green to indicate submitted
                  : isSubmitting
                  ? "#808080"
                  : "green",
                border: `1px solid ${currentTheme.border}`,
                color: "white",
                borderRadius: "4px",
                cursor:
                  isSubmitting || isQuestionSubmitted
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginLeft: "auto",
                opacity: isSubmitting || isQuestionSubmitted ? 0.7 : 1,
              }}
            >
              <span style={{ fontSize: "14px", color: "white" }}>
                {isSubmitting ? "⏳" : isQuestionSubmitted ? "✅" : "📥"}
              </span>
              {isSubmitting
                ? "Submitting..."
                : isQuestionSubmitted
                ? "Submitted"
                : "Submit"}
            </button>
            <button
              onClick={handleSkipQuestion}
              style={{
                padding: "4px 8px",
                backgroundColor: "lightgray",
                border: `1px solid ${currentTheme.border}`,
                color: "#000",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginLeft: "auto",
              }}
            >
              <span style={{ fontSize: "14px", color: "#000" }}>⏩</span>
              Skip
            </button>
          </div>
        </div>
      </div>
      {/* mainlayout */}
      <div
        className="layout"
        style={{
          height: console2 ? "75vh" : "100vh",
          paddingTop: "60px",
          width: "100%",
        }}
      >
        <Split
          direction="horizontal"
          style={{
            height: "100%",
            width: filesClick ? "calc(100% - 240px)" : "100%",
            display: "flex",
            transition: "width 0.4s ease",
            marginLeft: filesClick ? "240px" : "0",
          }}
          sizes={[15, 65, 50]}
          minSize={[200, 500, 280]}
          gutterSize={10}
          cursor="col-resize"
        >
          {/* Sidebar */}
          <div
            className="sidebar"
            style={{
              position: "fixed",
              top: "60px",
              left: 0,
              width: "260px",
              height: "calc(100% - 60px)",
              backgroundColor: currentTheme.secondary,
              padding: "1rem",
              borderRight: "1px solid #333",
              opacity: filesClick ? 1 : 0,
              zIndex: 20,
              transform: filesClick ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.3s ease-in-out, opacity 0.3s",
              pointerEvents: "auto",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ color: textColor }}>Files</h2>
              <button
                onClick={createNewFile}
                disabled={isQuestionSubmitted}
                style={{
                  background: isQuestionSubmitted ? "#808080" : "green",
                  color: "white",
                  borderRadius: 4,
                  padding: "4px 10px",
                  fontSize: "13px",
                  cursor: isQuestionSubmitted ? "not-allowed" : "pointer",
                  opacity: isQuestionSubmitted ? 0.7 : 1,
                }}
              >
                + New
              </button>
            </div>
            <ul style={{ listStyle: "none", padding: 0, marginTop: "1rem" }}>
              {Object.keys(files).map((file) => {
                const showDelete = file !== "App.js";
                return (
                  <li
                    key={file}
                    onClick={() => setCurrentFile(file)}
                    style={{
                      backgroundColor:
                        currentFile === file ? currentTheme.surface : "transparent",
                      color: currentFile === file ? currentTheme.text : currentTheme.textSecondary,
                      padding: "0.5rem",
                      borderRadius: 4,
                      marginBottom: 4,
                      fontSize: 13,
                      cursor: "pointer",
                      opacity: 1,
                      transition: "all 0.2s ease",
                      border: `1px solid ${currentFile === file ? currentTheme.border : 'transparent'}`,
                      "&:hover": {
                        backgroundColor: currentTheme.surface,
                        color: currentTheme.text,
                      }
                    }}
                  >
                    {file}
                    {showDelete && !isQuestionSubmitted && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteFile(file);
                        }}
                        style={{
                          float: "right",
                          background: "transparent",
                          color: currentTheme.textSecondary,
                          border: "none",
                          cursor: "pointer",
                          opacity: 0.7,
                          transition: "opacity 0.2s ease",
                          "&:hover": {
                            opacity: 1
                          }
                        }}
                      >
                        🗑️
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            <button
              onClick={handleEndCallClick}
              style={{
                position: "fixed",
                cursor: "pointer",
                bottom: 10,
                scale: 1,
                opacity: 1,
                pointerEvents: "auto",
              }}
              onMouseOver={(e) => (e.target.style.scale = "1.1")}
              onMouseOut={(e) => (e.target.style.scale = "1")}
            >
              <img src={callEndSvg} alt="End Call" />
            </button>
          </div>

          {/* Editor */}
          <div
            className="editor hidden-scrollbar"
            style={{
              background: currentTheme.secondary,
              overflow: "hidden",
              top: 10,
              transition: "margin-left 0.2s ease",
              boxShadow: `0 2px 4px ${
                editorTheme === "vs-dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(0,0,0,0.1)"
              }`,
            }}
          >
            {monaco && models[currentFile] && (
              <Editor
                theme={editorTheme}
                language="javascript"
                height="100%"
                path={currentFile}
                onMount={(editor, monaco) => {
                  const model = models[currentFile];
                  if (model) {
                    editor.setModel(model);
                    editor.revealPositionInCenter({ lineNumber: 1, column: 1 });
                  }

                  // Disable editor if question is submitted
                  if (isQuestionSubmitted) {
                    editor.updateOptions({ 
                      readOnly: true,
                      domReadOnly: true,
                      contextmenu: false,
                      quickSuggestions: false,
                      parameterHints: { enabled: false },
                      suggestOnTriggerCharacters: false,
                      formatOnPaste: false,
                      formatOnType: false,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      lineNumbers: "on",
                      renderLineHighlight: "all",
                      automaticLayout: true,
                      tabSize: 2,
                      padding: { top: 20, bottom: 20 },
                    });
                  }

                  const editorDomNode = editor.getDomNode();
                  if (editorDomNode) {
                    editorDomNode.addEventListener(
                      "paste",
                      (e) => {
                        if (!isQuestionSubmitted) {
                          e.preventDefault();
                          e.stopPropagation();
                          toast.error(
                            "Pasting code is not allowed in this editor"
                          );
                          alert("Pasting is disabled in this editor.");
                        }
                      },
                      true
                    );
                  }

                  editor.onKeyDown((e) => {
                    if (!isQuestionSubmitted && (e.ctrlKey || e.metaKey) && e.keyCode === monaco.KeyCode.KeyV) {
                      e.preventDefault();
                      e.stopPropagation();
                      toast.error("Pasting code is not allowed in this editor");
                      alert("Pasting is disabled in this editor.");
                    }
                  });

                  editor.addCommand(
                    monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV,
                    () => {
                      if (!isQuestionSubmitted) {
                        toast.error("Pasting code is not allowed in this editor");
                        alert("Pasting is disabled in this editor.");
                      }
                    }
                  );
                }}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  wordWrap: "on",
                  lineNumbers: "on",
                  renderLineHighlight: "all",
                  automaticLayout: true,
                  tabSize: 2,
                  padding: { top: 20, bottom: 20 },
                  formatOnPaste: false,
                  formatOnType: true,
                  quickSuggestions: !isQuestionSubmitted,
                  suggestOnTriggerCharacters: !isQuestionSubmitted,
                  parameterHints: { enabled: !isQuestionSubmitted },
                  suggest: {
                    showKeywords: !isQuestionSubmitted,
                    showSnippets: !isQuestionSubmitted,
                    showClasses: !isQuestionSubmitted,
                    showFunctions: !isQuestionSubmitted,
                    showVariables: !isQuestionSubmitted,
                  },
                  contextmenu: !isQuestionSubmitted,
                  readOnly: isQuestionSubmitted,
                }}
              />
            )}
          </div>

          {/* Preview */}
          <div
            className="preview"
            style={{
              padding: "1rem",
              width: "100%",
              backgroundColor: currentTheme.surface,
              color: currentTheme.text,
              borderLeft: `1px solid ${currentTheme.border}`,
              overflowY: "auto",
              boxShadow: `0 2px 4px ${
                editorTheme === "vs-dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(0,0,0,0.1)"
              }`,
            }}
          >
            <h3
              style={{
                textAlign: "center",
                fontSize: "16px",
                padding: "12px 0",
                letterSpacing: "0.5px",
                color: currentTheme.text,
                borderBottom: `1px solid ${currentTheme.border}`,
                marginBottom: "1rem",
                fontWeight: "500",
              }}
            >
              🔍 Output: App.js
            </h3>

            <div
              ref={previewRef}
              style={{ 
                marginTop: "5px", 
                color: currentTheme.text,
                padding: "1rem",
                backgroundColor: currentTheme.background,
                borderRadius: "8px",
                minHeight: "200px",
                display: "flex",
               heihgt:"100%"
              }}
            />

            {errors["App.js"] && (
              <div
                style={{
                  background: editorTheme === "vs-dark" ? "#3a1f1f" : "#fee2e2",
                  color: editorTheme === "vs-dark" ? "#fecaca" : "#991b1b",
                  padding: "1rem",
                  borderRadius: "8px",
                  marginTop: "1rem",
                  border: `1px solid ${editorTheme === "vs-dark" ? "#7f1d1d" : "#fecaca"}`,
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}>
                  <span>⚠️</span>
                  <span>Error</span>
                </div>
                <pre style={{ 
                  margin: 0, 
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                }}>
                  {errors["App.js"]}
                </pre>
              </div>
            )}
          </div>
        </Split>
      </div>

      {/* Console */}
      <div
        style={{
          transition: "all 0.s ease",
          boxShadow: `0 2px 4px ${
            editorTheme === "vs-dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)"
          }`,
        }}
      >
        {console2 ? (
          <div
            className="hidden-scrollbar"
            style={{
              position: "fixed",
              bottom: console2 ? 0 : "-25vh",
              left: 0,
              right: 0,
              backgroundColor: currentTheme.secondary,
              color: textColor,
              padding: "1rem",
              borderTop: "none",
              height: "25vh",
              overflowY: "auto",
              transition: filesClick
                ? "margin-left 0.3s ease-in-out"
                : "bottom 0.3s ease-in-out",
              marginLeft: filesClick ? "240px" : "0",
              boxShadow: `0 2px 4px ${
                editorTheme === "vs-dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(0,0,0,0.1)"
              }`,
            }}
          >
            <h3 style={{ color: textColor, marginBottom: "0.5rem" }}>
              Console
            </h3>
            <div style={{ fontSize: "0.85rem" }}>
              {logs.map((log, i) => {
                let color = log.color || textColor;

                // Auto-detect color based on log content
                const msg = log.message.toLowerCase();
                if (msg.includes("created and saved")) {
                  color = "green";
                } else if (msg.includes("deleted")) {
                  color = "red";
                } else if (msg.includes("changes saved successfully")) {
                  color = "yellow";
                }

                return (
                  <div
                    key={i}
                    style={{
                      marginBottom: "0.5rem",
                      color,
                    }}
                  >
                    <span
                      style={{
                        color: textColor,
                        marginRight: "0.5rem",
                        fontSize: 17,
                      }}
                    >
                      [ {log.timestamp} ]
                    </span>
                    <span style={{ fontSize: 17 }}>{log.message}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          ""
        )}
      </div>

      {/* File Operations Modal */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          pointerEvents: confirmModal.isOpen ? "auto" : "none",
          opacity: confirmModal.isOpen ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <ReactCodeEditorinput
          isOpen={confirmModal.isOpen}
          title={
            confirmModal.action === "delete"
              ? "Delete File"
              : confirmModal.action === "error"
              ? "Error"
              : "Create File"
          }
          message={
            confirmModal.action === "delete"
              ? `Are you sure you want to delete "${confirmModal.fileName}"?`
              : confirmModal.action === "error"
              ? confirmModal.errorMessage
              : `Enter new file name (e.g., MyFile.js, MyComponent.jsx):`
          }
          confirmLabel={
            confirmModal.action === "delete"
              ? "Delete"
              : confirmModal.action === "error"
              ? "OK"
              : "Create"
          }
          onConfirm={handleModalConfirm}
          onCancel={() =>
            setConfirmModal({ isOpen: false, action: null, fileName: null })
          }
          theme={editorTheme}
          backgroundColor={currentTheme.surface}
          textColor={currentTheme.text}
          borderColor={currentTheme.border}
        />
      </div>

      {/* Bot */}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 10000,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "flex-end",
          pointerEvents: "none",
        }}
      >
        <div
          className={`bot-container ${evaSpeaking ? "bot-speaking" : ""}`}
          style={{
            width: "300px",
            height: "120px",
            borderRadius: "16px",
            backgroundColor: "#fff",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            padding: "15px",
            backgroundImage: `url(${BotBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            overflow: "hidden",
            pointerEvents: "auto",
          }}
        >
          <div
            className="bot-circle"
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              border: evaSpeaking
                ? "1px solid #3b82f6"
                : "1px solid transparent",
              animation: evaSpeaking ? "flicker 1.5s infinite" : "none",
            }}
          >
            <img
              src={PromptHireLogo}
              alt="PromptHire Bot"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                objectFit: "cover",
                zIndex: 2,
              }}
            />
          </div>
          <div
            style={{
              marginLeft: "15px",
              flex: 1,
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: "#333",
                marginBottom: "8px",
              }}
            >
              Sarah
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                height: "20px",
                gap: "2px",
              }}
            >
              <div className={evaSpeaking ? "wave" : "disable-wave"}></div>
              <div className={evaSpeaking ? "wave" : "disable-wave"}></div>
              <div className={evaSpeaking ? "wave" : "disable-wave"}></div>
              <div className={evaSpeaking ? "wave" : "disable-wave"}></div>
              <div className={evaSpeaking ? "wave" : "disable-wave"}></div>
              <div className={evaSpeaking ? "wave" : "disable-wave"}></div>
              <div className={evaSpeaking ? "wave" : "disable-wave"}></div>
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                marginTop: "8px",
              }}
            >
              {evaSpeaking
                ? "Listening to your response..."
                : "Waiting for your input..."}
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      <div>
        <ConfirmationModal
          isOpen={isSkipModalOpen}
          onClose={handleCloseSkipModal}
          onConfirm={handleSkipConfirm}
          modalType="skip_question"
        />
        <ConfirmationModal
          isOpen={isSubmitModalOpen}
          onClose={handleCloseSubmitModal}
          onConfirm={handleSubmitConfirm}
          modalType="submit_code"
        />
        <ConfirmationModal
          isOpen={isEndCallModalOpen}
          onClose={handleCloseEndCallModal}
          onConfirm={handleEndCallConfirm}
          modalType="end_call"
        />
        <ConfirmationModal
          isOpen={isHintModalOpen}
          onClose={handleCloseHintModal}
          onConfirm={() => handleHintSelection(true)}
          modalType="hint_selection"
          cancelCallback={() => handleHintSelection(false)}
          hintCount={hintCount}
          maxHints={maxHints}
        />
      </div>
    </div>
  );
}

